import { useCallback } from 'react';

import { DialogField } from '@sitecore/types/OfferStep';
import { RichTextField, TextField } from '@sitecore-jss/sitecore-jss-react';

interface ProductDetails {
  title?: string;
  promotionText?: string;
  tariffDescription?: string;
  tariffSplitExplanation?: string;
  usps: string[];
  unitPriceLabels?: ProductUnitPriceLabels;
  tagLabel?: string;
  endOfContractInformationDialog: DialogField;
  electricityDeliveryRateDescription?: TextField;
  gasDeliveryRateDescription?: TextField;
  productBenefitsDescription?: string;
  productInformationDialog?: DialogField;
}

interface ProductUnitPriceLabels {
  electricityHighLabel?: string;
  electricityLabel?: string;
  electricityLowLabel?: string;
  gasLabel?: string;
  redeliveryLabel?: string;
  warmthLabel?: string;
  waterColdLabel?: string;
  waterWarmLabel?: string;
}

export interface Data {
  byPeriodLabel?: TextField;
  id: TextField;
  usp1Text?: TextField;
  usp2Text?: TextField;
  usp3Text?: TextField;
  usp4Text?: TextField;
  usp1Content?: RichTextField;
  usp2Content?: RichTextField;
  usp3Content?: RichTextField;
  name?: TextField;
  promotionText?: TextField;
  tariffDescriptionText?: TextField;
  tariffSplitExplanationText?: TextField;
  endOfContractInformationDialog?: DialogField;
  electricityDeliveryRateDescription?: TextField;
  gasDeliveryRateDescription?: TextField;
  productBenefitsDescription?: TextField;
  productInformationDialog?: DialogField;
}
interface OfferData {
  id: string;
  url: string;
  name: string;
  displayName: string;
  fields: {
    data?: Data;
    unitPriceLabels?: {
      electricityHighLabel?: TextField;
      electricityLabel?: TextField;
      electricityLowLabel?: TextField;
      gasLabel?: TextField;
      redeliveryLabel?: TextField;
      warmthLabel?: TextField;
      waterColdLabel?: TextField;
      waterWarmLabel?: TextField;
      tagLabel?: TextField;
    };
  };
  [k: string]: unknown;
}

export const useProductItemContent = (textualData?: OfferData[]) => {
  const doesItemExist = useCallback(
    (key: string) => {
      if (!textualData) {
        return false;
      }
      return textualData?.some(i => i.fields?.data?.id?.value === key);
    },
    [textualData],
  );

  const getProductDetails: (key: string) => ProductDetails | null = useCallback(
    (key: string) => {
      // Check for existence for safety, in case consumer doesn't check using 'doesItemExist'
      if (!doesItemExist(key)) {
        return null;
      }

      const productItem = textualData!.find(i => i.fields?.data?.id?.value === key);

      // Unwrap values
      return {
        title: productItem?.fields?.data?.name?.value,
        promotionText: productItem?.fields?.data?.promotionText?.value,
        tariffDescription: productItem?.fields?.data?.tariffDescriptionText?.value,
        tariffSplitExplanation: productItem?.fields?.data?.tariffSplitExplanationText?.value,
        endOfContractInformationDialog: productItem?.fields?.data?.endOfContractInformationDialog,
        electricityDeliveryRateDescription: productItem?.fields?.data?.electricityDeliveryRateDescription,
        gasDeliveryRateDescription: productItem?.fields?.data?.gasDeliveryRateDescription,
        productBenefitsDescription: productItem?.fields?.data?.productBenefitsDescription?.value,
        productInformationDialog: productItem?.fields?.data?.productInformationDialog,
        unitPriceLabels: {
          electricityLabel: productItem?.fields?.unitPriceLabels?.electricityLabel?.value || undefined,
          electricityHighLabel: productItem?.fields?.unitPriceLabels?.electricityHighLabel?.value || undefined,
          electricityLowLabel: productItem?.fields?.unitPriceLabels?.electricityLowLabel?.value || undefined,
          gasLabel: productItem?.fields?.unitPriceLabels?.gasLabel?.value || undefined,
          warmthLabel: productItem?.fields?.unitPriceLabels?.warmthLabel?.value || undefined,
          redeliveryLabel: productItem?.fields?.unitPriceLabels?.waterWarmLabel?.value || undefined,
          waterColdLabel: productItem?.fields?.unitPriceLabels?.waterColdLabel?.value || undefined,
          waterWarmLabel: productItem?.fields?.unitPriceLabels?.waterWarmLabel?.value || undefined,
        },
        tagLabel: productItem?.fields?.unitPriceLabels?.tagLabel?.value || undefined,
        // TODO: Allowing both Text and Content USP fields for backwards compatibility, giving preference to Content. Remove after Text is not used anymore.
        usps: !productItem?.fields?.data
          ? []
          : (() => {
              const entries = Object.entries(productItem.fields.data);
              const hasContentValues = entries.some(
                ([key, value]) => key.includes('Content') && key.startsWith('usp') && value.value,
              );
              const suffix = hasContentValues ? 'Content' : 'Text';
              return entries
                .filter(([key]) => key.startsWith('usp') && key.includes(suffix))
                .filter(([_, value]) => Boolean(value.value))
                .map(([_, value]) => value.value);
            })(),
      };
    },
    [doesItemExist, textualData],
  ) as (key: string) => ProductDetails | null;

  return { doesItemExist, getProductDetails };
};
